import {jsx, ParamComponent} from 'panel';

import {Section, SectionItem} from 'common/components/items-menu/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {
  DEFAULT_SEARCH_QUERY_STRING,
  getBrowseUrl,
  PROMPT_ALLOW_STORAGE_STRING,
  searchUtils,
} from 'common/report/util/search';
import {TypeaheadTrackedEvent} from 'common/report/util/search/tracking';
import {RecentSearchItemType} from 'common/report/util/search/types';
import {KEY_VALUES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {generateShortcutLabel, getCommandKey, keyEventSwitcher} from 'common/util/keyboard';
import {Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';
import {DropMenuEl} from 'common/components/drop-menu';
import {InputEl} from 'common/components/input';
import {InputTheme} from 'common/components/input/types';
import {ItemsMenuEl} from 'common/components/items-menu';
import {useEventListener} from 'common/util/component-helpers';
import {IronContextRegistry} from 'common/report/context-registry';

import css from './index.inline.styl';

const RECENT_TERM_TYPE = `mp-search-input-recent-term`;

type SearchInputState = {
  allowRecentItems?: boolean;
  open: boolean;
  searchInputEl?: HTMLElement;
};

export type SearchInputParams<ItemType = any> = {
  enableShortcut?: boolean;
  actionText?: string;
  onEnterPressed?: () => void;
  onItemClick?: (item: SectionItem<ItemType>) => void;
  onSearchedTextUpdate?: (text: string, isSuggestedItem?: boolean) => void;
  onClearClick?: () => void;
  onMenuOpen?: () => void;
  onTrackEvent?: (event: string, props?: Obj) => void;
  placeholder?: string;
  searchedText?: string;
  sections?: Array<Section<ItemType>>;
  theme?: InputTheme;
};

type SearchInputJsxProps = {
  params: SearchInputParams;
};

export class SearchInput extends ParamComponent<SearchInputParams, SearchInputState, IronContextRegistry> {
  get config() {
    return {
      useShadowDom: true,
      css,
      contexts: [`urlManager` as const, `currentProject` as const],
      template: () => {
        return (
          <div sel='.search-input-container'>
            <InputEl
              sel='.search-input'
              attrs={{
                'action-text': this.params.actionText ?? ``,
                icon: `brand-search`,
                placeholder: this.getSearchPlaceholder(),
                'show-clear': true,
                theme: this.params.theme,
                elref: `searchInput`,
              }}
              props={{value: this.params.searchedText}}
              on={{
                input: (ev) => this.handleSearchInputChange(ev.target.value),
                clear: () => {
                  this.handleSearchInputChange(DEFAULT_SEARCH_QUERY_STRING);
                  this.params.onClearClick?.();
                },
                click: () => this.openSearchMenu(),
                focus: () => this.openSearchMenu(),
              }}
              hook={{
                insert: (vnode) => this.handleSearchInputInserted(vnode.elm),
              }}
            />
            {this.state.open && !!this.getSections()?.length && (
              <DropMenuEl
                attrs={{
                  open: true,
                  'prevent-close-selectors': [`mp-input.search-input`],
                  'disable-auto-focus': true,
                }}
                on={{change: (ev: VisibilityChangeEvent) => this.handleDropDownVisibilityChange(ev)}}
                style={{width: `${this.offsetWidth}px`}}
              >
                <ItemsMenuEl
                  attrs={{elref: `searchInputItemsMenu`}}
                  props={{
                    sections: this.getSections(),
                    keydownProxyEl: this.state.searchInputEl,
                  }}
                  on={{
                    clickItem: (ev) => this.handleSearchSelection(ev),
                  }}
                />
              </DropMenuEl>
            )}
          </div>
        );
      },
      defaultState: {
        allowRecentItems: true,
        open: false,
      },
      params: {
        actionText: String,
        enableShortcut: Boolean,
        onEnterPressed: Function,
        onItemClick: Function,
        onMenuOpen: Function,
        onSearchedTextUpdate: Function,
        onTrackEvent: Function,
        onClearClick: Function,
        placeholder: String,
        searchedText: String,
        sections: Array,
        theme: String,
      },
      defaultParams: {
        searchedText: ``,
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.params.enableShortcut) {
      useEventListener(this, document, `keydown`, this.chromeHotKeySwitcher);
    }

    /**
     * Binding the `keydown` listener to this component rather than the mp-input we render,
     * since the mp-input is used as a keydown proxy for mp-items-menu. If mp-items-menu is unable to deal with
     * the keydown event, it will bubble up to this component's handler.
     * Otherwise, we have two keydown listeners on mp-input and we won't be able to guarantee order of execution.
     */
    useEventListener(this, this, `keydown`, (ev: KeyboardEvent) => this.handleSearchInputKeydown(ev));
    this.updateAllowStorage();
  }

  handleSearchInputInserted(el) {
    this.update({searchInputEl: el});
  }

  handleDropDownVisibilityChange(ev: VisibilityChangeEvent) {
    const open = ev.detail.state === Visibility.Open;
    this.update({open});

    if (open === false) {
      this.state.searchInputEl?.blur();
    }
  }

  handleSearchInputChange(searchedText: string, isSuggestedItem = false) {
    this.params.onSearchedTextUpdate?.(searchedText, isSuggestedItem);
  }

  handleSearchInputKeydown(ev: KeyboardEvent) {
    if (ev.code === KEY_VALUES.ENTER && this.params.onEnterPressed) {
      ev.preventDefault();
      ev.stopPropagation();
      this.params.onEnterPressed();
      this.closeSearchMenu();
    }
  }

  handleSearchSelection(ev) {
    const item = ev.detail;
    if (item.type === RECENT_TERM_TYPE) {
      this.handleSearchInputChange(item.label, true);
      this.addRecentSearchItem(item.label);
    } else if (!item.isReadOnly) {
      this.params.onItemClick?.(item);

      if (this.params.searchedText !== DEFAULT_SEARCH_QUERY_STRING) {
        this.addRecentSearchItem(this.params.searchedText);
      }
    }
    this.closeSearchMenu();
  }

  closeSearchMenu(): void {
    this.update({open: false});
    this.state.searchInputEl?.blur();
  }

  openSearchMenu(): void {
    if (!this.state.open) {
      this.update({open: true});
      this.params.onMenuOpen?.();
    }
    this.state.searchInputEl?.focus();
  }

  getSearchPlaceholder(): string {
    const placeholder = this.params.placeholder ?? `Search`;
    if (this.params.enableShortcut) {
      const shortcutLabel = generateShortcutLabel(`K`, {modifiers: [getCommandKey()]});
      return `${placeholder}  ${shortcutLabel}`;
    }

    return placeholder;
  }

  getSections() {
    const isDefault = this.params.searchedText === DEFAULT_SEARCH_QUERY_STRING;
    return [...(isDefault ? this.getRecentTermsSection() : []), ...(this.params.sections ?? [])];
  }

  private async updateAllowStorage() {
    await window.gdprData?.waitUntilReady();
    this.update({allowRecentItems: window.gdprData?.allowStorage});
  }

  private addRecentSearchItem(text: string) {
    searchUtils.addRecentItem({
      icon: `search`,
      key: `${RecentSearchItemType.SearchTerm}-${text}`,
      label: text,
      type: RecentSearchItemType.SearchTerm,
    });
  }

  private getRecentTermsSection(): Section[] {
    const {allowRecentItems} = this.state;
    const recentSearchTerms = searchUtils
      .getRecentItems()
      ?.filter((item) => item.type === RecentSearchItemType.SearchTerm);

    const urlManager = this.getContext(`urlManager`);
    const items: SectionItem[] =
      recentSearchTerms?.length > 0
        ? recentSearchTerms.slice(0, 3).map(
            (item): SectionItem => {
              return {
                label: item.label,
                type: RECENT_TERM_TYPE,
                value: item,
                href: getBrowseUrl(urlManager, {queryParam: {q: item.label}}),
              };
            },
          )
        : [
            {
              isReadOnly: true,
              label: allowRecentItems ? `No recent history` : PROMPT_ALLOW_STORAGE_STRING,
              value: ``,
            },
          ];

    return [
      {
        label: `Recently Searched`,
        isLoading: false,
        hideTopDivider: true,
        actionsRenderer: (item) => {
          if (item.isReadOnly) {
            return null;
          }

          return (
            <SvgIconEl
              attrs={{icon: `x`, elref: `removeRecentItemIcon`}}
              on={{
                click: (ev) => {
                  // prevent items menu select handler from firing
                  ev.preventDefault();
                  ev.stopPropagation();
                  searchUtils.removeRecentItem(item.value);
                  this.update();
                  this.params.onTrackEvent?.(TypeaheadTrackedEvent.RecentResultDeleted);
                },
              }}
            />
          );
        },
        items,
      },
    ];
  }

  private chromeHotKeySwitcher = (ev) => {
    return keyEventSwitcher(
      {
        [`${getCommandKey()} + KeyK`]: () => {
          this.openSearchMenu();
        },
      },
      {stopPropagation: true, usableInInputs: true},
    )(ev);
  };
}

export const SearchInputEl = defineAndMakeJsxFactory<SearchInputJsxProps>(`mp-search-input`, SearchInput);
